/** Review (what needs a look), the ranch log, and one record with its revisions. */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import type { CaptureDTO, EventDTO } from "../../../contract";
import { api } from "../../lib/api";
import { formatDate, timeAgo, useFormat } from "../../lib/format";
import { plural, t, useLanguage } from "../../lib/i18n";
import { invalidateAll, useApi } from "../../lib/query";
import { useCan, useMe } from "../../lib/session";
import { openRecord } from "../../forms/EventForm";
import { eventView, registry } from "../../registry";
import { Button, Card, Chip, Empty, ErrorNote, Icon, Kbd, Loading, PageHeader, Section, Tabs, TextInput, cx, isSheetOpen, prefersReducedMotion, toast, useIsWide } from "../../ui/kit";
import { DataList, eventSummary } from "../describe";
import { Page } from "../Layout";
import { ProposalCard, isClearNote, type ProposalActions } from "../ProposalCard";
import { Timeline } from "../Timeline";
import { NotFound } from "./NotFound";

// ---------------------------------------------------------------- keyboard review

export type ReviewKeyAction = "next" | "previous" | "file" | "edit" | "reject" | "help";

/** Shortcut keys for the review queue: J/K move, Enter files, E edits, X is "Not this", ? shows help. */
export function reviewKeyAction(e: Pick<KeyboardEvent, "key" | "metaKey" | "ctrlKey" | "altKey">): ReviewKeyAction | null {
  if (e.metaKey || e.ctrlKey || e.altKey) return null;
  switch (e.key) {
    case "j": case "J": return "next";
    case "k": case "K": return "previous";
    case "Enter": return "file";
    case "e": case "E": return "edit";
    case "x": case "X": return "reject";
    case "?": return "help";
    default: return null;
  }
}

/** Typing in a field, or pressing a button, keeps its own keys. */
function ownsKeys(target: EventTarget | null, key: string): boolean {
  const el = target as HTMLElement | null;
  if (!el || !el.closest) return false;
  if (el.closest("input, textarea, select, [contenteditable='true'], [role='combobox'], [role='listbox'], [role='menu']")) return true;
  // Enter on a focused button or link means that button, not "file this note".
  if (key === "Enter" && el.closest("button, a[href], summary, audio")) return true;
  return false;
}

export function ReviewPage() {
  useLanguage();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") ?? "needs";
  const setTab = (id: string) => setParams(id === "needs" ? {} : { tab: id }, { replace: true });
  const status = tab === "needs" ? "needs_review" : tab === "working" ? "queued,processing,uploading,failed" : "filed,answered,discarded";
  const q = useApi<{ items: CaptureDTO[] }>(`/captures?status=${status}&limit=50`, { refetchInterval: tab === "working" ? 2000 : 15_000 });
  const items = q.data?.items ?? [];
  const wide = useIsWide();
  const [active, setActive] = useState(0);
  const [help, setHelp] = useState(false);
  const [bulk, setBulk] = useState(false);
  const cards = useRef(new Map<string, ProposalActions | null>());
  const clear = tab === "needs" ? items.filter(isClearNote) : [];
  const keyboard = tab === "needs" && items.length > 0;

  // Stay on the same position when the card in it is filed and leaves the list.
  useEffect(() => { if (active > items.length - 1) setActive(Math.max(0, items.length - 1)); }, [items.length, active]);

  const focusCard = useCallback((index: number) => {
    const id = items[index]?.id;
    const el = id ? document.getElementById(`proposal-${id}`) : null;
    if (!el) return;
    el.focus({ preventScroll: true });
    el.scrollIntoView?.({ block: "nearest", behavior: prefersReducedMotion() ? "auto" : "smooth" });
  }, [items]);

  useEffect(() => {
    if (!keyboard) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || isSheetOpen() || ownsKeys(e.target, e.key)) return;
      const action = reviewKeyAction(e);
      if (!action) return;
      const current = items[active];
      const card = current ? cards.current.get(current.id) : null;
      e.preventDefault();
      if (action === "next") { const n = Math.min(active + 1, items.length - 1); setActive(n); focusCard(n); }
      else if (action === "previous") { const n = Math.max(active - 1, 0); setActive(n); focusCard(n); }
      else if (action === "file") void card?.file();
      else if (action === "edit") card?.edit();
      else if (action === "reject") card?.reject();
      else if (action === "help") setHelp((h) => !h);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [keyboard, items, active, focusCard]);

  const fileAllClear = async () => {
    setBulk(true);
    const filed: string[] = [];
    try {
      for (const c of clear) {
        try { await api.post(`/proposals/${c.proposal!.id}/file`, {}); filed.push(c.proposal!.id); } catch { /* left for a look */ }
      }
      await invalidateAll();
      const missed = clear.length - filed.length;
      toast(missed ? t("Filed {{count}}. {{missed}} still need a look.", { count: filed.length, missed }) : plural(filed.length, "Filed {{count}} note", "Filed {{count}} notes"), {
        tone: missed ? "warn" : "good",
        action: filed.length ? { label: t("Undo"), run: () => void Promise.all(filed.map((id) => api.post(`/proposals/${id}/undo`))).then(invalidateAll).then(() => toast(t("Undone. They're waiting for a look."))) } : undefined,
      });
    } finally { setBulk(false); }
  };

  return (
    <Page>
      <PageHeader title={t("Notes to check")} subtitle={t("Check what Hondo understood. One tap files it.")}
        actions={clear.length > 1 && <Button variant="primary" icon="check" busy={bulk} onClick={() => void fileAllClear()}>{plural(clear.length, "File {{count}} clear note", "File all {{count}} clear notes")}</Button>} />
      <Tabs value={tab} onChange={setTab} tabs={[{ id: "needs", label: t("Needs a look"), count: tab === "needs" ? items.length : undefined }, { id: "working", label: t("In progress") }, { id: "done", label: t("Done") }]} />
      {keyboard && wide && (
        <p className={cx("shortcut-bar", help && "is-open")}>
          <span><Kbd>J</Kbd><Kbd>K</Kbd> {t("move")}</span>
          <span><Kbd>↵</Kbd> {t("file")}</span>
          <span><Kbd>E</Kbd> {t("edit")}</span>
          <span><Kbd>X</Kbd> {t("not this")}</span>
          <button type="button" className="link small" onClick={() => setHelp((h) => !h)} aria-expanded={help}><Kbd>?</Kbd> {t("help")}</button>
          {help && <span className="shortcut-help">{t("Click a fact to fix it in place. Filed notes can be undone from the message that appears.")}</span>}
        </p>
      )}
      {clear.length > 0 && clear.length < items.length && tab === "needs" && (
        <p className="muted small">{t("Clear notes have no open questions and nothing that always needs a person, like new animals, sales, deaths, money or stock.")}</p>
      )}
      {q.isLoading ? <Loading /> : q.error && !q.data ? <ErrorNote error={q.error} retry={() => q.refetch()} /> : !items.length ? (
        <Empty icon="done" title={tab === "needs" ? t("Nothing waiting") : t("Nothing here")}
          body={tab === "needs" ? t("When Hondo isn't sure about something, it waits here. Try saying “Moved 42 to the North pasture.”") : undefined} />
      ) : (
        <div className="stack review-list">
          {items.map((c, i) => (
            <div key={c.id} onFocusCapture={() => { if (i !== active) setActive(i); }} onMouseDown={() => { if (i !== active) setActive(i); }}>
              <ProposalCard capture={c} active={keyboard && wide && i === active}
                actions={(a) => { if (a) cards.current.set(c.id, a); else cards.current.delete(c.id); }} />
            </div>
          ))}
        </div>
      )}
    </Page>
  );
}

export function LogPage() {
  useLanguage();
  return (
    <Page>
      <PageHeader back={{ to: "/", label: t("Today") }} title={t("Ranch log")} subtitle={t("Everything recorded, newest first.")} />
      <Timeline />
    </Page>
  );
}

export function EventPage() {
  const { id } = useParams();
  const q = useApi<{ event: EventDTO; revisions: EventDTO[] }>(`/events/${id}`);
  const can = useCan();
  const { data: me } = useMe();
  const [reason, setReason] = useState("");
  const [removing, setRemoving] = useState(false);
  const e = q.data?.event;
  const fmt = useFormat(e?.labels ?? {});
  if (q.isLoading) return <Loading />;
  if (!e) return q.error && (q.error as { status?: number }).status === 404 ? <NotFound /> : <Page><ErrorNote error={q.error} retry={() => q.refetch()} /></Page>;
  const def = registry.event(e.type);
  const Detail = eventView(e.type)?.Detail;
  const mine = e.recorded_by?.id === me?.membership?.id;
  const editable = e.status === "active" && (can("manage") || (mine && can("record")));
  const remove = async () => {
    await api.post(`/events/${e.id}/void`, { reason: reason || null });
    await invalidateAll();
    toast(t("Removed. It stays in history."));
    setRemoving(false);
  };
  return (
    <Page>
      <PageHeader back={e.subjects[0]?.kind === "animal" ? { to: `/cattle/${e.subjects[0].id}?tab=history`, label: e.subjects[0].label ?? t("Back") } : { to: "/history", label: t("Ranch log") }}
        eyebrow={<span className="eyebrow-icon"><Icon name={def?.icon ?? "note"} size={16} />{def ? t(def.label) : e.type}</span>}
        title={eventSummary(e, fmt)}
        subtitle={[formatDate(e.occurred_at, e.occurred_precision), e.recorded_by ? t("recorded by {{name}} {{when}}", { name: e.recorded_by.label, when: timeAgo(e.recorded_at) }) : null].filter(Boolean).join(" · ")}
        actions={editable && <>
          <Button icon="edit" onClick={() => openRecord({ initial: { type: e.type, subjects: e.subjects, occurred: { value: e.occurred_at ?? undefined, precision: e.occurred_precision, end: e.occurred_end ?? undefined }, data: e.data, place_id: e.place_id, job_id: e.job_id }, existing: e, onDone: (fresh) => { location.href = `/history/${fresh.id}`; } })}>{t("Correct")}</Button>
          <Button variant="ghost" icon="delete" onClick={() => setRemoving(true)}>{t("Remove")}</Button>
        </>}>
        <div className="animal-status">
          {e.status === "superseded" && <Chip tone="info" icon="edit">{t("Replaced by a correction")}</Chip>}
          {e.status === "voided" && <Chip tone="neutral" icon="archive">{t("Removed")}{e.reason ? `: ${e.reason}` : ""}</Chip>}
          {e.revision > 1 && e.status === "active" && <Chip tone="info" icon="edit">{t("Corrected")}{e.reason ? `: ${e.reason}` : ""}</Chip>}
          {e.source_kind === "capture" && e.source_id && <Chip tone="neutral" icon="mic">{t("From a note")}</Chip>}
          {e.source_kind === "import" && <Chip tone="neutral" icon="upload">{t("Imported")}</Chip>}
        </div>
      </PageHeader>
      {removing && (
        <Card className="confirm">
          <p>{t("Remove this record? It stays in history and can be seen later, but no longer counts.")}</p>
          <TextInput value={reason} onChange={(ev) => setReason(ev.target.value)} placeholder={t("Why? Duplicate, wrong animal…")} aria-label={t("Reason")} autoFocus />
          <div className="row-actions"><Button variant="danger" onClick={remove}>{t("Remove")}</Button><Button variant="ghost" onClick={() => setRemoving(false)}>{t("Keep it")}</Button></div>
        </Card>
      )}
      <Section title={t("Who and what")}>
        <div className="subject-links">
          {e.subjects.map((s) => <Link key={`${s.role}${s.id}`} className="chip tone-neutral" to={s.kind === "animal" ? `/cattle/${s.id}` : s.kind === "place" ? `/ranch/places/${s.id}` : s.kind === "group" ? `/cattle/groups/${s.id}` : s.kind === "asset" ? `/ranch/equipment/${s.id}` : s.kind === "contact" ? `/ranch/people/${s.id}` : s.kind === "job" ? `/work/jobs/${s.id}` : "#"}>
            <Icon name={s.kind === "animal" ? "animal" : s.kind} size={14} />{def?.subjects.find((x) => x.role === s.role) ? `${t(def.subjects.find((x) => x.role === s.role)!.label)}: ` : ""}{s.label}
          </Link>)}
        </div>
      </Section>
      {Detail ? <Detail event={e} /> : def && <Card><DataList fields={def.fields} data={e.data} fmt={fmt} /></Card>}
      {e.evidence && Object.values(e.evidence).some((b) => b !== "stated") && (
        <p className="muted evidence">{t("Some details were filled in: {{list}}", { list: Object.entries(e.evidence).filter(([, b]) => b !== "stated").map(([k, b]) => `${def?.fields[k] ? t(def.fields[k].label) : k} (${t(b)})`).join(", ") })}</p>
      )}
      {q.data!.revisions.length > 1 && (
        <Section title={t("Changes")}>
          <ol className="revisions">{q.data!.revisions.map((r) => (
            <li key={r.id} className={r.id === e.id ? "is-current" : undefined}>
              <Link to={`/history/${r.id}`}>{t("Version {{n}}", { n: r.revision })}</Link> · {timeAgo(r.recorded_at)}{r.recorded_by ? ` · ${r.recorded_by.label}` : ""}{r.reason ? ` · ${r.reason}` : ""}
            </li>
          ))}</ol>
        </Section>
      )}
    </Page>
  );
}

export { Timeline };
